import React, { useEffect } from 'react';
import _ from 'lodash';

// ui
import { Box, Card, CardContent, Grid } from '@mui/material';

import ToggleButton from '@mui/lab/ToggleButton';
// helpers
import { withStyles } from '@mui/styles';

import classnames from 'classnames';
import { FormProvider, useForm } from 'react-hook-form';
import { withRouter } from '../../../../utility/routeUtils';

import { PubSub } from '../../../../utils/eventUtils';
// actions
import {
  updateSecurityQuestions,
  updateOrgProfile,
} from '../../../../accounts/AccountService';
// components
import CBButton from '../../../Buttons/CbButton';
import { InputLabelBase } from '../../../inputs/InputLabelBase';
import { RegisteredToggleButtonGroup } from '../../../Buttons/registered/v7/RegistedToggleButtonGroup';
import { EditSection } from '../../../../public/quote/confirm/EditSection';
import { Modal } from '../../../modals/v2/helpers/v2.modal.helpers';
import { InvalidateQuotesConfig } from '../../../modals/InvalidateQuotes';
import { FirmoAndSecurityHeader } from '../../../../console/agencies/quotes/PrimeX/AccountDetails/components/SecurityAssessment';
import useNotifications from '../../../../_lib/notifications/useNotifications';
import { useAPIErrorHandler } from '../../../hooks/useAPIErrorHandler';

const buttonStyles = {
  width: '150px',
  margin: `1.66rem 0 1.66rem 0`,
};

const LabelProps = {
  required: true,
  TypographyProps: {
    variant: 'body2',
    gutterBottom: true,
  },
};

const P100SecurityQuestions = withRouter(
  ({
    location,
    classes,
    accountId,
    canVerify,
    inData,
    from,
    readOnly = false,
    togglePrompt,
    onSubmitSuccess,
    onSubmitHandler,
  }: any) => {
    const toggleButtonProps = {
      disabled: readOnly,
      className: classes.toggleButton,
      classes: {
        disabled: classes.toggleButtonDisabled,
      },
    };
    const notifications = useNotifications();
    const handleAPIError = useAPIErrorHandler();

    const orgProfile = from === 'orgProfile';
    const attestation = from === 'attestation';
    const screen = location.pathname.split('/')[1];
    const customer = screen === 'customer';

    const data = inData || {};

    // dont add defaut values to below, they need to be determined in getValue func even with empty data
    const defaultValues = React.useMemo(
      () => ({
        isSecurityOfficer: _.get(inData, 'isSecurityOfficer', null),
        isSecurityTraining: _.get(inData, 'isSecurityTraining', null),
        claimHistory: _.get(inData, 'claimHistory', null),
        useEncryption: _.get(inData, 'useEncryption', null),
        useCloudStorage: _.get(inData, 'useCloudStorage', null),
        priorCoverage: _.get(inData, 'priorCoverage', null),
      }),
      [inData]
    );

    const { handleSubmit, ...methods } = useForm({
      defaultValues,
    });

    const {
      formState: { isDirty, isSubmitting },
      reset,
    } = methods;

    useEffect(() => {
      reset(defaultValues);
    }, [reset, defaultValues]);

    useEffect(() => {
      const sub = PubSub.subscribe('cancel-detailChanges', (res: any) => {
        if (res) {
          reset(defaultValues);
        }
      });
      return () => {
        sub.remove();
      };
      // eslint-disable-next-line
    }, []);

    const updateOrganisationProfile = (payload: any) => {
      updateOrgProfile({}, payload)
        .then((resp: any) => {
          notifications.success('Security Assessment Updated Successfully.');
        })
        .catch((error: any) => {
          reset(defaultValues);
          handleAPIError('Something went wrong.Please try agin later.')(error);
        });
    };

    const updatingQuestions = (payload: any) => {
      updateSecurityQuestions({}, payload)
        .then(() => {
          notifications.show({
            type: 'success',
            title: 'Security questions saved.',
            position: 'bottom-right',
          });

          onSubmitSuccess?.();

          PubSub.publish('disable-Submit', false);
          PubSub.publish('fetch-details', true);
          PubSub.publish('org:agent-attested', {
            isVerifiedByAgent: false,
          });
        })
        .catch((error: any) => {
          reset(defaultValues);
          handleAPIError('Something went wrong.Please try again later.')(error);
        });
    };

    const onSubmit = ({
      isSecurityOfficer,
      isSecurityTraining,
      claimHistory,
      useEncryption,
      useCloudStorage,
      ...formData
    }: any) => {
      const { name, naicsCode, revenue } = data;
      const payload = {
        isSecurityOfficer,
        isSecurityTraining,
        useEncryption,
        useCloudStorage,
        claimHistory,
        accountId,
        name,
        naicsCode,
        revenue,
        ...formData,
      };

      if (typeof onSubmitHandler == 'function') {
        return onSubmitHandler(payload);
      }

      if (!orgProfile && isDirty && customer) {
        Modal.show(InvalidateQuotesConfig, {
          data: {
            update: 'questions',
            reset,
            ...payload,
          },
          config: {
            title: `Account Changes for ${data.name}`,
          },
        });
      } else if (orgProfile) {
        updateOrganisationProfile(payload);
      } else {
        updatingQuestions({ ...payload, renewalChangeInAnswers: true });
      }

      reset({
        isSecurityOfficer,
        isSecurityTraining,
        claimHistory,
        useEncryption,
        useCloudStorage,
        ...formData,
      });
    };

    const handleCancel = () => {
      reset(defaultValues);
    };

    const verifyIcon = 'VerifiedTabYes.svg';
    const verifyElement = canVerify ? (
      <Box className={classes.verifyTabIcon}>
        <img
          src={`images/${verifyIcon}`}
          alt=""
          className={classes.iconStyle}
        />
      </Box>
    ) : null;

    useEffect(() => {
      if (togglePrompt) {
        togglePrompt('P100_SECURITY_QUESTIONS', isDirty);
      }
      // eslint-disable-next-line
    }, [isDirty]);

    return (
      <FormProvider {...methods} handleSubmit={handleSubmit}>
        <Card className={orgProfile ? classes.cardContainer : classes.card}>
          <CardContent
            className={`form-section-wrapper ${classnames({
              [classes.cardContentContainer]: orgProfile,
            })}`}
          >
            <form onSubmit={handleSubmit(onSubmit)}>
              {!orgProfile && (
                <div className="flex--spaced" style={{ paddingBottom: '1rem' }}>
                  <FirmoAndSecurityHeader>
                    Security Assessment
                  </FirmoAndSecurityHeader>
                </div>
              )}
              <section className={orgProfile && classes.inactiveBackground}>
                <Grid container justifyContent="left">
                  <Grid
                    container
                    justifyContent="left"
                    className={
                      orgProfile ? classes.inputFields : classes.attestInput
                    }
                  >
                    <Grid item md={12}>
                      <div>
                        <InputLabelBase
                          TypographyProps={{
                            variant: 'body2',
                            gutterBottom: true,
                          }}
                          style={{ paddingTop: 0 }}
                        >
                          Does the organization currently carry an insurance
                          policy that insures against a Cyber loss?
                        </InputLabelBase>
                        <RegisteredToggleButtonGroup
                          name="priorCoverage"
                          exclusive
                        >
                          <ToggleButton value="YES" {...toggleButtonProps}>
                            <div>Yes</div>
                          </ToggleButton>
                          <ToggleButton value="NO" {...toggleButtonProps}>
                            <div>No</div>
                          </ToggleButton>
                          <ToggleButton
                            value="UNCERTAIN"
                            {...toggleButtonProps}
                          >
                            <div>Uncertain</div>
                          </ToggleButton>
                        </RegisteredToggleButtonGroup>
                      </div>
                    </Grid>
                    <Grid item md={12}>
                      <div>
                        <InputLabelBase {...LabelProps}>
                          Have you ever had any cyber insurance claim(s)?
                        </InputLabelBase>
                        <RegisteredToggleButtonGroup
                          name="claimHistory"
                          exclusive
                        >
                          <ToggleButton value={0} {...toggleButtonProps}>
                            <div>Never</div>
                          </ToggleButton>
                          <ToggleButton value={1} {...toggleButtonProps}>
                            <div>Within last 12 months</div>
                          </ToggleButton>
                          <ToggleButton value={2} {...toggleButtonProps}>
                            <div>Within last 2 years</div>
                          </ToggleButton>
                          <ToggleButton value={3} {...toggleButtonProps}>
                            <div>Within last 3 years</div>
                          </ToggleButton>
                          <ToggleButton value={4} {...toggleButtonProps}>
                            <div>Within last 4 years</div>
                          </ToggleButton>
                          <ToggleButton value={5} {...toggleButtonProps}>
                            <div>within 5 years or more</div>
                          </ToggleButton>
                        </RegisteredToggleButtonGroup>
                      </div>
                    </Grid>
                    <Grid item md={12}>
                      <div>
                        <InputLabelBase {...LabelProps}>
                          Does your organization have a Designated Information
                          Security Contact?
                        </InputLabelBase>
                        <RegisteredToggleButtonGroup
                          name="isSecurityOfficer"
                          exclusive
                        >
                          <ToggleButton value {...toggleButtonProps}>
                            <div>Yes</div>
                          </ToggleButton>
                          <ToggleButton value={false} {...toggleButtonProps}>
                            <div>No</div>
                          </ToggleButton>
                        </RegisteredToggleButtonGroup>
                      </div>
                    </Grid>
                    <Grid item md={12}>
                      <div>
                        <InputLabelBase {...LabelProps}>
                          Does your organization provide mandatory information
                          security training to all employees at least annually?
                          If not, are you willing to implement it during the
                          policy period?
                        </InputLabelBase>
                        <RegisteredToggleButtonGroup
                          name="isSecurityTraining"
                          exclusive
                        >
                          <ToggleButton value {...toggleButtonProps}>
                            <div>Yes</div>
                          </ToggleButton>
                          <ToggleButton value={false} {...toggleButtonProps}>
                            <div>No</div>
                          </ToggleButton>
                        </RegisteredToggleButtonGroup>
                      </div>
                    </Grid>
                    <Grid item md={12}>
                      <div>
                        <InputLabelBase {...LabelProps}>
                          Does your organization encrypt emails, mobile and
                          computing devices containing sensitive information
                          (e.g., PII, PHI, PCI) sent to external parties?
                        </InputLabelBase>
                        <RegisteredToggleButtonGroup
                          name="useEncryption"
                          exclusive
                        >
                          <ToggleButton value {...toggleButtonProps}>
                            <div>Yes</div>
                          </ToggleButton>
                          <ToggleButton value={false} {...toggleButtonProps}>
                            <div>No</div>
                          </ToggleButton>
                        </RegisteredToggleButtonGroup>
                      </div>
                    </Grid>
                    <Grid item md={12}>
                      <div>
                        <InputLabelBase {...LabelProps}>
                          Does your organization have sensitive information
                          stored on the cloud?
                        </InputLabelBase>
                        <RegisteredToggleButtonGroup
                          name="useCloudStorage"
                          exclusive
                        >
                          <ToggleButton value {...toggleButtonProps}>
                            <div>Yes</div>
                          </ToggleButton>
                          <ToggleButton value={false} {...toggleButtonProps}>
                            <div>No</div>
                          </ToggleButton>
                        </RegisteredToggleButtonGroup>
                      </div>
                    </Grid>
                  </Grid>
                </Grid>
                {!orgProfile && attestation && customer && !readOnly && (
                  <Grid
                    container
                    justifyContent="center"
                    className={classes.buttonContainer}
                  >
                    <Grid item md={4} className={classes.leftBtn}>
                      <CBButton
                        size="large"
                        styleName="cancel"
                        style={buttonStyles}
                        onClick={handleCancel}
                        disabled={isSubmitting || !isDirty}
                      >
                        Cancel
                      </CBButton>
                    </Grid>
                    <Grid item md={4}>
                      <CBButton
                        type="submit"
                        size="large"
                        styleName="ctaButton"
                        style={buttonStyles}
                        loading={isSubmitting}
                        disabled={isSubmitting || !isDirty}
                      >
                        Save
                      </CBButton>
                    </Grid>
                  </Grid>
                )}
                {!orgProfile && !(attestation && customer) && (
                  <EditSection show={!readOnly} />
                )}
                {orgProfile && (
                  <Grid
                    container
                    justifyContent="center"
                    className={classes.buttonContainer}
                  >
                    <Grid item md={4} className={classes.leftBtn}>
                      <CBButton
                        size="large"
                        styleName="cancel"
                        style={buttonStyles}
                        onClick={handleCancel}
                        disabled={isSubmitting || !isDirty}
                      >
                        Cancel
                      </CBButton>
                    </Grid>
                    <Grid item md={4}>
                      <CBButton
                        type="submit"
                        size="large"
                        styleName="ctaButton"
                        style={buttonStyles}
                        loading={isSubmitting}
                        disabled={isSubmitting}
                      >
                        Save Changes
                      </CBButton>
                    </Grid>
                  </Grid>
                )}
                {verifyElement}
              </section>
            </form>
          </CardContent>
        </Card>
      </FormProvider>
    );
  }
);

const styles = ({ palette, config }: any) => {
  return {
    heading: {
      fontWeight: config.weights.bold,
      lineHeight: 1.38,
      marginTop: 0,
    },

    cardContainer: {
      padding: '0px 10px 0px 10px',
    },
    leftBtn: {
      textAlign: 'right',
      paddingRight: 40,
    },

    buttonContainer: {
      borderTop: `1px solid ${config.colors.darkGreySoft}`,
      justifyContent: 'space-evenly',
      '& .MuiGrid-item': {
        padding: '0px 48px 0 0',
      },
    },
    cardContentContainer: {
      padding: '5px 0px 5px 0px !important',
      borderTop: `1px solid ${config.colors.darkGreySoft}`,
    },

    inputFields: {
      width: '95%',
      float: 'left',
      margin: '0 0 2.5rem 0 !important',
      justifyContent: 'space-evenly',
      '& .MuiGrid-item': {
        padding: '2rem 0 0 8rem',
        display: 'flex',
      },
    },
    attestInput: {
      margin: '0 !important',
      overflow: 'auto',
      '& .MuiGrid-item': {
        padding: '.8rem 0 .2rem 0',
        display: 'flex',
      },
    },
    inactiveBackground: {
      overflow: 'hidden auto',
      padding: '2.5rem 0 0 0',
      backgroundColor: palette.background.paper,
      position: 'relative',
    },
    verifyTabIcon: {
      position: 'absolute',
      right: 0,
      top: '40%',
      cursor: 'pointer',
    },
    iconStyle: {
      paddingLeft: 5,
      paddingRight: 5,
      width: '45px',
      height: '70px',
    },
    toggleButton: {
      padding: '0.5rem 4rem',
    },
    toggleButtonDisabled: {
      color: `${palette.primary.contrastText} !important`,
    },
  };
};

// @ts-expect-error TS(2345): Argument of type '({ palette, config }: any) => { ... Remove this comment to see the full error message
export default withStyles(styles)(P100SecurityQuestions);
